import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'
import { CATALOG_SORT } from 'lib/core/resources/constants'
import { useCoreContext } from 'lib/core/hooks'
import { VisitureProductsQueryParams } from 'lib/types'
import { getProductAttributeList } from 'lib/core/data/common/productAttributeList/selectors'
import { getCatalogSortDefault } from 'lib/core/data/common/storeConfig/selectors'
import { normalizeProduct } from 'lib/modules/catalog'
import { RootState } from 'lib/core/data/reducer'

interface Params {
  search: string
  pageSize: number
  minLength: number
  enable?: boolean
}

export const useSearchSuggestions = (params: Params) => {
  const { pageSize, minLength, enable } = params
  const search = params.search.trim()
  const { magentoClient } = useCoreContext()
  const productAttrList = useSelector(getProductAttributeList)
  const catalogSortDefault = useSelector(getCatalogSortDefault)
  const storeConfig = useSelector((state: RootState) => state.common.storeConfig.payload)

  /**
   * Service Query
   */
  const queryParams: VisitureProductsQueryParams = {
    search,
    sort: {
      [catalogSortDefault]: CATALOG_SORT.ASC
    },
    pageSize,
    currentPage: 1,
    filter: {}
  }

  const { data: response, isLoading, isFetching, isError } = useQuery(
    ['useSearchSuggestions', queryParams], () => {
      return magentoClient?.catalog.fetchProductListPage(queryParams)
    }, {
      enabled: enable && (search.length >= minLength)
    }
  )

  /**
   * Products Data
   */
  const visitureProducts = response?.data?.data?.visitureProducts
  const data = useMemo(() => {
    return {
      /**
       * Products
       */
      products: visitureProducts?.items.map(product => normalizeProduct({
        product,
        productAttributeList: productAttrList,
        storeConfig
      })),

      /**
       * Has Items
       */
      hasItems: Boolean(visitureProducts?.items.length)
    }
  }, [visitureProducts, productAttrList, storeConfig])

  /**
   * Hook API
   */
  return {
    isLoading,
    isFetching,
    isError,
    hasItems: data.hasItems,
    products: data.products,
    totalCount: visitureProducts?.total_count
  }
}
